import React, { useState, useEffect, useMemo } from "react";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftAvatar from "components/SoftAvatar";
import swal from "sweetalert2";
import bruce from "assets/images/bruce-mars.jpg";
import { api } from "utils/Api";
import EditFormPopup from "layouts/admin/user/data/EditForm";
import DeleteForm from "layouts/admin/Organization/DeleteForm";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SmartTableCard from "utils/Smart Table/SmartTableCard";
import ManagerAddPropertyPopup from "./AdminManagerAddToProperty";
import jwtDecode from "jwt-decode";
import { getSecureItem } from "utils/SecureStorage";
import { useParams } from "react-router-dom";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { Box, Tooltip } from "@mui/material";
import AdminManagerAddPropertyPopup from "./AdminManagerAddToProperty";
import RemoveFromParentForm from "layouts/admin/Organization/MultipleDeleteForm";
import { showSuccess } from "utils/messageAlerts";
import { canCreate } from "utils/permissionManager";
import { canUpdate } from "utils/permissionManager";
import { canDelete } from "utils/permissionManager";
import { useAuth } from "context/AuthContext";
import { useHierarchy } from "context/HierarchyContext";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import MultiConfirmDialog from "utils/MultiConfirmDialog";

function AdminManagers() {
  const [managers, setManagers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isManagerAddPopupOpen, setIsManagerAddPopupOpen] = useState(false);
  const [popupMode, setPopupMode] = useState("create");
  const [selectedManager, setSelectedManager] = useState(null);
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const currentOrgId = JSON.parse(getSecureItem("currentUser"))?.organization_id;
  const roleId = JSON.parse(getSecureItem("currentUser"))?.role_id;
  const [propertyFilterAnchor, setPropertyFilterAnchor] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [propertySearch, setPropertySearch] = useState("");
  const {isManager, isAdmin} = useAuth();
  const { propertyId } = useHierarchy();
  const current_user_role_id = getSecureItem("role_id");
  const { orgId } = useHierarchy();
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null,
    manager: null,
    property: null,
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchManagers();
    }, 10);

    return () => clearTimeout(timer);
  }, [propertyId]);

  const fetchProperties = async () => {
    try {
      const propertyData = await api.properties.getProperties({ organization_id: orgId || currentOrgId, current_user_role_id });
      setProperties(propertyData);
    } catch (e) {
      
    }
  }; 

  // Fetch all users, then filter only role_id === 3 (Managers)
  const fetchManagers = async () => {
    let role_id = 3;
    let userApi;

    try {
      userApi = propertyId == null
        ? await api.user.getFiltertedUser({
          role_id,
          organization_id: parseInt(orgId) || currentOrgId,
          current_user_role_id
        }) || []
        : await api.user.getFiltertedUserByProperty({
          role_id,
          organization_id: parseInt(orgId) || currentOrgId,
          property_id: propertyId,
          current_user_role_id
        }) || [];
      const formatted = userApi.map((u) => ({
        id: u.id,
        username: u.username,
        first_name: u.first_name,
        last_name: u.last_name,
        email: u.email,
        phone_number: u.phone_number,
        description: u.description,
        active: u.active,
        image: u.image || null,
        organization_id: u.organization_id,
        role_id: u.role_id,
        createdAt: u.created_at,
        property_id: u.property_id,
        user_property: u.user_property || [],
      }));
      setManagers(formatted);
    } catch (e) {
      setManagers([]);
    }
  };

  const handleEditManager = (manager) => {
    setPopupMode("edit");
    setSelectedManager(manager);
    setIsPopupOpen(true);
  };

  const handleDeleteManager = async (payload) => {
    try {
      await api.userProperty.deleteUserProperty(payload);
      showSuccess("Manager removed from property successfully");
      fetchManagers();
    } catch (err) {}
  };

  // Search Managers
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const filteredManagers = managers.filter((manager) => {
    const term = searchTerm.toLowerCase();

    const matchesSearch =
      !searchTerm.trim() ||
      manager.first_name?.toLowerCase().includes(term) ||
      manager.last_name?.toLowerCase().includes(term) ||
      manager.email?.toLowerCase().includes(term) ||
      manager.username?.toLowerCase().includes(term);

    const matchesProperty =
      selectedProperties.length === 0 ||
      manager.user_property?.some((up) => selectedProperties.includes(up.property_id));

    return matchesSearch && matchesProperty;
  });

  const filteredPropertyList = properties
    .filter((prop) => prop.organization_id === Number(orgId)) // filter by orgId
    .filter((prop) => prop.name.toLowerCase().includes(propertySearch.toLowerCase()));

  const propertyMap = useMemo(() => {
    const map = {};
    properties.forEach((p) => {
      map[p.id] = p.name;
    });
    return map;
  }, [properties]);

  const canEdit = canUpdate("User Property");
  const canRemove = canDelete("User Property");
  const showActionsColumn = canEdit || canRemove;

  const handleManagerActionClick = (user, action) => {
    setConfirmDialog({
      open: true,
      type: action,
      user,
    });
  };

  const handleConfirmAction = async (property_id) => {
    const { manager, type } = confirmDialog;

    if (!property_id) return;

    const payload = {
      property_id,
      user_id: manager.id,
      user_role_id: 3,
      organization_id: orgId || currentOrgId,
    };

    try {
      if (type === "delete") {
        await api.userProperty.deleteUserPropertyHard(payload);
        showSuccess("Manager permanently deleted successfully");
      } else {
        await api.userProperty.restoreUserProperty({
          property_id,
          user_id: manager.id,
          organization_id: orgId || currentOrgId,
          current_user_role_id: roleId,
        });
        showSuccess("Manager restored successfully");
      }

      fetchManagers();
    } catch (e) {}

    setConfirmDialog({ open: false, type: null, manager: null, property: null });
  };

  return (
    <SmartTableCard
      title={`All Managers`}
      headerRight={[
        !isManager &&
        canCreate("User Property") && (
          <SoftButton
            key="add-btn"
            variant="outlined"
            color="info"
            size="small"
            onClick={() => setIsManagerAddPopupOpen(true)}
          >
            Add Manager
          </SoftButton>
        ),

        <SoftInput
          key="search-input"
          placeholder="Search Manager"
          value={searchTerm}
          onChange={handleSearch}
          size="small"
        />,
      ]}
      columns={[
        // { label: "Manager", key: "Manager" },
        { label: "Username", key: "Username", align: "center" },
        { label: "Email", key: "Email", align: "center" },
        { label: "Phone", key: "Phone", align: "center" },
        { label: "Description", key: "Description", align: "center" },
        // { label: "Property", key: "PropertyID", align: "center" },
        {
          label: (
            <SoftBox display="flex" alignItems="center" justifyContent="center" gap="4px">
              <span>Property</span>

              <SoftBox position="relative" display="flex" alignItems="center">
                <FilterAltOutlinedIcon
                  fontSize="small"
                  sx={{
                    cursor: "pointer",
                    color: selectedProperties.length ? "#0284C7" : "#475467",
                    "&:hover": { color: "#0284C7" },
                  }}
                  onClick={(e) => setPropertyFilterAnchor(e.currentTarget)}
                />

                {selectedProperties.length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -4,
                      right: -6,
                      background: "#0284C7",
                      color: "#fff",
                      borderRadius: "50%",
                      fontSize: 10,
                      width: 16,
                      height: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {selectedProperties.length}
                  </Box>
                )}
              </SoftBox>
            </SoftBox>
          ),
          key: "PropertyID",
          align: "center",
        },
        ...(isAdmin || isManager ? [{ label: "Active", key: "Active", align: "center" }] : []),
        ...(showActionsColumn && !isManager ? [{ label: "Actions", key: "Actions", align: "center" }] : []),
      ]}
      rows={filteredManagers.map((manager) => {
        const validUserProperties = (manager.user_property || []).filter(
          (uu) => propertyMap?.[uu.property_id]
        );

        const isActive = validUserProperties.some((uu) => uu.active === true);
        const isInactive = !isActive;

        return {
          Username: manager.username,
          Email: manager.email,
          Phone: manager.phone_number,
          Description: manager.description,
          PropertyID: (() => {
            if (!manager.user_property?.length) return "N/A";

            const propertyNames = manager.user_property
              .filter((up) => up.user_id === manager.id)
              .map((up) => propertyMap[up.property_id])
              .filter(Boolean);

            return propertyNames.length ? propertyNames.join(", ") : "N/A";
          })(),
          ...(isAdmin || isManager
            ? {
                Active: (() => {
                  const inactiveProperty = manager.user_property?.find((uu) => uu.active === false);
                  const activeProperty = manager.user_property?.find((uu) => uu.active === true);

                  // ❌ If any inactive → show actions
                  if (inactiveProperty) {
                    return (
                      <SoftBox display="flex" justifyContent="center" gap="6px">
                        {/* Restore */}
                        <Tooltip title="Restore Manager" arrow>
                          <IconButton
                            onClick={() =>
                              setConfirmDialog({
                                open: true,
                                type: "restore",
                                manager,
                                property: inactiveProperty,
                              })
                            }
                            sx={{ color: "#16A34A" }}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Delete */}
                        <Tooltip title="Delete Permanently" arrow>
                          <IconButton
                            onClick={() =>
                              setConfirmDialog({
                                open: true,
                                type: "delete",
                                manager,
                                property: inactiveProperty,
                              })
                            }
                            sx={{ color: "#DC2626" }}
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </SoftBox>
                    );
                  }

                  // ✅ Otherwise show active
                  if (activeProperty) {
                    return (
                      <SoftTypography variant="button" color="success" fontWeight="bold">
                        Yes
                      </SoftTypography>
                    );
                  }

                  return "N/A";
                })(),
              }
            : {}),
          ...(showActionsColumn && {
            Actions: (
              <SoftBox display="flex" justifyContent="center" gap="8px">
                {(isAdmin) && canEdit && (
                  <IconButton
                    disabled={isInactive}
                    onClick={() => handleEditManager(manager)}
                    sx={{
                      border: "1px solid #D0D5DD",
                      backgroundColor: "#F9FAFB",
                      width: 34,
                      height: 34,
                      "& svg": { fontSize: 18 },
                      color: "#475467",
                      "&:hover": {
                        backgroundColor: "#E0F2FE",
                        borderColor: "#38BDF8",
                        color: "#0284C7",
                      },
                    }}
                  >
                    <EditIcon fontSize="sm" />
                  </IconButton>
                )}

                {(isAdmin) && canRemove && (
                  <IconButton
                    onClick={() => {
                      setDeletePopupOpen(true);
                      setSelectedManager(manager);
                    }}
                    disabled={isInactive}
                    sx={{
                      border: "1px solid #FECACA",
                      backgroundColor: "#FEF2F2",
                      width: 34,
                      height: 34,
                      "& svg": { fontSize: 18 },
                      color: "#B91C1C",
                      "&:hover": {
                        backgroundColor: "#FEE2E2",
                        borderColor: "#F87171",
                        color: "#991B1B",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="sm" />
                  </IconButton>
                )}
              </SoftBox>
            ),
          }),
        };
      })}
    >
      {/* {filteredManagers.length === 0 && (
        <div style={{ margin: "15px", fontSize: "17px", textAlign: "center" }}>
          No managers found
        </div>
      )} */}

      <MultiConfirmDialog
        open={confirmDialog.open}
        type={confirmDialog.type}
        title={confirmDialog.type === "delete" ? "Delete Manager" : "Restore Manager"}
        description={
          confirmDialog.type === "delete"
            ? "This will permanently delete the manager. This action cannot be undone."
            : "Are you sure you want to restore this manager?"
        }
        confirmText={confirmDialog.type === "delete" ? "Delete Permanently" : "Restore"}
        onClose={() => setConfirmDialog({ open: false, type: null, manager: null, property: null })}
        onConfirm={handleConfirmAction}
        assignments={confirmDialog.manager?.user_property || []}
        parentMap={propertyMap}
        idKey="property_id"
        entityLabel="Property"
      />

      {isPopupOpen && (
        <EditFormPopup
          mode={popupMode}
          user={selectedManager}
          onClose={() => {
            setIsPopupOpen(false);
            fetchManagers();
          }}
        />
      )}

      {isManagerAddPopupOpen && (
        <AdminManagerAddPropertyPopup
          onClose={() => setIsManagerAddPopupOpen(false)}
          refreshData={fetchManagers}
        />
      )}

      {/* {deletePopupOpen && (
        <DeleteForm
          data={selectedManager}
          onClose={() => setDeletePopupOpen(false)}
          onDelete={(id) => {
            handleDeleteManager(id);
            setDeletePopupOpen(false);
          }}
        />
      )} */}
      {deletePopupOpen && (
        // <RemoveFromParentForm
        //   data={selectedManager}
        //   onClose={() => setDeletePopupOpen(false)}
        //   onDelete={(payload) => {
        //     handleDeleteManager(payload);
        //     setDeletePopupOpen(false);
        //   }}
        // />
        <RemoveFromParentForm
          open={deletePopupOpen}
          entity={selectedManager}
          entityLabel="Manager"
          parentLabel="Property"
          assignments={selectedManager?.user_property}
          parentKey="property_id"
          parentMap={propertyMap}
          onClose={() => setDeletePopupOpen(false)}
          onRemove={(property_id, user) => {
            handleDeleteManager({
              property_id,
              user_id: user.id,
              user_role_id: 3,
              organization_id: orgId || currentOrgId,
            });
            setDeletePopupOpen(false);
          }}
        />
      )}

      <Menu
        anchorEl={propertyFilterAnchor}
        open={Boolean(propertyFilterAnchor)}
        onClose={() => setPropertyFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {/* Search */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={1}
          py={1}
          gap={1}
        >
          <SoftInput
            placeholder="Search property"
            size="small"
            value={propertySearch}
            onChange={(e) => setPropertySearch(e.target.value)}
          />

          <IconButton
            size="small"
            onClick={() => {
              setPropertyFilterAnchor(null);
              setPropertySearch(""); // optional: reset search
            }}
          >
            ✕
          </IconButton>
        </Box>

        {/* Clear */}
        {selectedProperties.length > 0 && (
          <MenuItem
            onClick={() => setSelectedProperties([])}
            sx={{ color: "#0284C7", fontWeight: 600 }}
          >
            Clear Selection
          </MenuItem>
        )}

        {/* Property List */}
        {filteredPropertyList.length > 0 ? (
          filteredPropertyList.map((prop) => (
            <MenuItem
              key={prop.id}
              onClick={() => {
                if (selectedProperties.includes(prop.id)) {
                  setSelectedProperties(selectedProperties.filter((id) => id !== prop.id));
                } else {
                  setSelectedProperties([...selectedProperties, prop.id]);
                }
              }}
            >
              <Checkbox checked={selectedProperties.includes(prop.id)} />
              <ListItemText primary={prop.name} primaryTypographyProps={{ variant: "body3" }} />
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No properties found</MenuItem>
        )}
      </Menu>
    </SmartTableCard>
  );
}

export default AdminManagers;